import { memo, useRef, useMemo, useCallback, useEffect, useState, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Crud, CrudMode, notification, rejectionError, transformCrudFormErrors, SelectsOptions, getSelectsOptions } from '@ohpen/enso-ui';
import type { CrudFormComponent, CrudProps, ValidationError } from '@ohpen/enso-ui';
import {
  ChangeRequestOfLenderFirmDraftDto,
  lenderFirmsGetLenderFirm,
  lenderFirmsCreateChangeRequest,
  FirmGetAll,
  FirmGetById,
} from '@pmp/api';
import { crudToActionMap, changeRequestNotificationMessage, changeRequestButtonMessage } from '@pmp/utils';
import { mapLenderFirmFormValuesToDraft } from '../utils';
import { useFeatureToggle } from '@pmp/hooks';
import { CrudFormProps } from '@pmp/types';

import { FormSchema } from '.';

export const FirmsCrudForm = memo(
  ({ mode: modeProp, record, valueLists, refreshTable, onClose }: CrudFormProps<FirmGetAll>): ReactElement | null => {
    const ref = useRef<CrudFormComponent>(null);
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState<CrudMode | null>(modeProp);
    const { t } = useTranslation();
    const enableFourEyes = useFeatureToggle(['enableFourEyes']);

    useEffect(() => {
      if (mode === CrudMode.EDIT && !record?.id) {
        notification().error({ message: t('global.errors.unexpected') });
        onClose();
        return;
      }
      setOpen(true);
    }, [mode, record, t, onClose]);

    const request = useCallback(async ({ firmId, mode }: { firmId: string; mode: CrudMode }) => {
      if (mode === CrudMode.CREATE) return {};
      const { data } = await lenderFirmsGetLenderFirm({ path: { firmId } });
      return {
        ...data,
        tradingNames: data?.tradingNames?.map(tn => ({
          ...tn,
          isActive: (tn as Record<string, unknown>).isActive ?? true,
        })) ?? [],
      };
    }, []);

    const mapFormValuesToDraft = useCallback(
      (values: FirmGetById) => mapLenderFirmFormValuesToDraft(values, record),
      [record],
    );

    const onConfirm: (formData: FirmGetById) => Promise<ValidationError[] | void> = useCallback(
      (values: FirmGetById) => {
        if (!mode || !crudToActionMap[mode]) return Promise.reject(rejectionError('mode is required'));

        const data = mapFormValuesToDraft(values);
        const body: ChangeRequestOfLenderFirmDraftDto = {
          data,
          actionType: crudToActionMap[mode],
        };

        return lenderFirmsCreateChangeRequest({ body }).then(
          ({ data: responseData }: { data: { isValid?: boolean; errors?: Record<string, string[]> } }) => {
            if (!responseData.isValid) {
              const form = ref.current?.form;
              if (form) {
                const errorFields = form.getFieldsError().map(f => ({ name: f.name, errors: [] }));
                if (errorFields.length) form.setFields(errorFields);
              }

              return transformCrudFormErrors({ ...responseData.errors });
            }
            if (mode === CrudMode.EDIT) refreshTable();
            notification().success({
              message: changeRequestNotificationMessage(t, enableFourEyes),
            });
          },
        );
      },
      [mode, refreshTable, t, enableFourEyes, mapFormValuesToDraft],
    );

    const handleOnClose = useCallback(() => {
      setOpen(false);
      setTimeout(() => {
        onClose();
      }, 300);
    }, [onClose]);

    const title = useMemo(() => {
      if (!mode) return '';
      const titles: { [key: string]: string } = {
        [CrudMode.VIEW]: t('global.labels.viewX', { x: t('lender.firms.labels.firm') }),
        [CrudMode.CREATE]: t('global.labels.createX', { x: t('lender.firms.labels.firm') }),
        [CrudMode.EDIT]: t('global.labels.editX', { x: t('lender.firms.labels.firm') }),
      };
      return titles[mode];
    }, [mode, t]);

    const selectsOptions: SelectsOptions = useMemo(() => (valueLists && getSelectsOptions(valueLists)) || {}, [valueLists]);

    const crudProps: CrudProps<FirmGetById> = useMemo(() => {
      return {
        ref,
        open,
        title,
        labels: {
          confirm: mode === CrudMode.EDIT ? t('global.labels.save') : changeRequestButtonMessage(t, enableFourEyes),
        },
        params: { firmId: record?.id, mode },
        request,
        mode,
        customForm: true,
        width: 1100,
        wrapperCol: { flex: 'auto' },
        disableDrawerBodyScroll: true,
        onClose: handleOnClose,
        onConfirm,
      };
    }, [open, title, mode, t, record?.id, request, handleOnClose, onConfirm, enableFourEyes]);

    return (
      <Crud {...crudProps}>
        <FormSchema form={ref.current?.form} mode={mode ?? CrudMode.VIEW} selectsOptions={selectsOptions} />
      </Crud>
    );
  },
);
